
import Vue from 'vue';
import {
  GET_DAILY_SHIPPING_SUMMARY,
  GET_DAILY_SHIPMENTS,
  CREATE_CARRIER_MANIFEST,
  GET_UPS_PACKAGE_SUMMARY
} from '@/gql/shipper';
import UpsPackageSummaryPrint from '@/components/Shipper/UpsPackageSummaryPrint.vue';
import { hasRole, hasAnyRole, WellKnownRoles } from '@/models/User';
import UpsLogo from '@/assets/svg-icons/ups-logo.svg';
import FedexLogo from '@/assets/svg-icons/fedex-logo.svg';
import UspsLogo from '@/assets/svg-icons/usps-logo.svg';
import { getHardwareService, HardwareIntegrationService } from '@/services/HardwareIntegrationService';
import { getPrinterConfiguration, PrinterConfigurationService } from '@/services/PrinterConfigurationService';

interface CarrierSummary {
  carrierCode: string;
  carrierName: string;
  shipmentCount: number;
  totalCost: number;
  manifestStatus: string;
  manifestId: string | null;
  manifestDownloadUrl: string | null;
  unmanifestedLabelCount: number;
  supportsApiManifest: boolean;
  manifestInstructions: string | null;
}

interface DailySummary {
  reportDate: string;
  labelsCreated: number;
  labelsVoided: number;
  returnLabelsCreated: number;
  pickTicketsShipped: number;
  totalShippingCost: number;
  pendingManifestCount: number;
  carrierBreakdown: CarrierSummary[];
}

interface Shipment {
  shippingLabelId: number;
  labelId: string;
  createdAt: string;
  pickTicketId: number;
  orderId: number;
  orderNumber: string;
  customerName: string;
  carrierCode: string;
  carrierName: string;
  serviceCode: string;
  serviceName: string;
  trackingNumber: string;
  trackingUrl: string;
  shippingCost: number;
  isVoided: boolean;
  isReturnLabel: boolean;
  isManifested: boolean;
  // Multi-package shipment fields
  shipmentId: string | null;
  packageSequence: number | null;
  totalPackages: number | null;
  isMultiPackage: boolean;
}

export default Vue.extend({
  name: 'EndOfDay',

  components: {
    UpsPackageSummaryPrint
  },

  data() {
    return {
      loading: false,
      loadingShipments: false,
      reportDate: (() => {
        // Use local date, not UTC (toISOString uses UTC which can be a day ahead)
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      })(),
      dateMenu: false,
      summary: null as DailySummary | null,
      shipments: [] as Shipment[],
      totalShipments: 0,
      search: '',
      carrierFilter: null as string | null,
      tableOptions: {
        page: 1,
        itemsPerPage: 25,
        sortBy: [],
        sortDesc: []
      },
      shipmentHeaders: [
        { text: 'Time', value: 'createdAt', width: '90px' },
        { text: 'Order #', value: 'orderNumber', width: '100px' },
        { text: 'Pick Ticket', value: 'pickTicketId', width: '120px' },
        { text: 'Customer', value: 'customerName' },
        { text: 'Carrier', value: 'carrierCode', width: '120px' },
        { text: 'Tracking #', value: 'trackingNumber' },
        { text: 'Pkg', value: 'package', width: '70px', align: 'center', sortable: false },
        { text: 'Cost', value: 'shippingCost', width: '80px', align: 'right' },
        { text: 'Status', value: 'status', width: '100px', align: 'center' }
      ],
      manifestDialog: false,
      selectedCarrier: null as CarrierSummary | null,
      creatingManifest: null as string | null,
      // UPS Package Summary
      upsPackageSummaryDialog: false,
      upsPackageSummary: null as any,
      loadingUpsSummary: false,
      // Print options
      printOptionsMenu: false,
      printingToHardware: false,
      hardwareService: null as HardwareIntegrationService | null,
      printerConfigService: null as PrinterConfigurationService | null,
      configuredPrinterId: null as string | null,
      configuredPrinterName: null as string | null,
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      searchDebounceTimer: null as number | null,
      carrierLogos: {
        ups: UpsLogo,
        fedex: FedexLogo,
        fedex_express: FedexLogo,
        fedex_ground: FedexLogo,
        fedex_walleted: FedexLogo,
        usps: UspsLogo,
        stamps_com: UspsLogo,
      } as Record<string, string>
    };
  },

  computed: {
    reportDateFormatted(): string {
      if (!this.reportDate) return '';
      const date = new Date(this.reportDate + 'T00:00:00');
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    carrierFilterOptions(): { text: string; value: string }[] {
      if (!this.summary?.carrierBreakdown) return [];
      return this.summary.carrierBreakdown.map(c => ({
        text: c.carrierName,
        value: c.carrierCode
      }));
    },

    hasPendingManifests(): boolean {
      if (!this.summary?.carrierBreakdown) return false;
      // Only count carriers that support API manifests
      return this.summary.carrierBreakdown.some(
        c => c.supportsApiManifest && (c.manifestStatus === 'Pending' || c.manifestStatus === 'Partial')
      );
    },

    pendingCarrierCount(): number {
      if (!this.summary?.carrierBreakdown) return 0;
      return this.summary.carrierBreakdown.filter(
        c => c.supportsApiManifest && (c.manifestStatus === 'Pending' || c.manifestStatus === 'Partial')
      ).length;
    },

    pendingManifestInfo(): string {
      if (!this.summary?.carrierBreakdown) return '';
      // Only include carriers that support API manifests
      const pending = this.summary.carrierBreakdown.filter(
        c => c.supportsApiManifest && (c.manifestStatus === 'Pending' || c.manifestStatus === 'Partial')
      );
      if (pending.length === 0) return '';
      if (pending.length === 1) {
        return `${pending[0].unmanifestedLabelCount} ${pending[0].carrierName} shipment${pending[0].unmanifestedLabelCount > 1 ? 's' : ''}`;
      }
      const totalCount = pending.reduce((sum, c) => sum + c.unmanifestedLabelCount, 0);
      const carrierNames = pending.map(c => c.carrierName).join(', ');
      return `${totalCount} shipments (${carrierNames})`;
    },

    isToday(): boolean {
      // Use local date, not UTC
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const todayLocal = `${year}-${month}-${day}`;
      return this.reportDate === todayLocal;
    },

    /**
     * Check if the current user can navigate to order pages.
     * Shipper-only users cannot access order pages, but admins can.
     */
    canViewOrders(): boolean {
      const user = this.$store.getters.userInfo;
      if (!user) return false;
      // Admin and Super Admin can view orders
      if (hasAnyRole(user, [WellKnownRoles.ADMIN, WellKnownRoles.SUPER_ADMIN])) {
        return true;
      }
      // Shipper-only users (have Shipper role but not Admin) cannot view orders
      if (hasRole(user, WellKnownRoles.SHIPPER)) {
        return false;
      }
      // Other users with order view permissions can view orders
      return true;
    }
  },

  mounted() {
    this.loadSummary();
    this.loadShipments();
    this.initializeHardwareServices();
  },

  methods: {
    onDateChange() {
      this.dateMenu = false;
      this.tableOptions.page = 1; // Reset to first page
      this.refreshAll();
    },

    refreshAll() {
      this.loadSummary();
      this.loadShipments();
    },

    /**
     * Convert report date to local midnight ISO string
     * This ensures the server interprets the date in local timezone
     */
    getReportDateISO(): string {
      // Append T00:00:00 to treat as local midnight, then convert to ISO
      return new Date(this.reportDate + 'T00:00:00').toISOString();
    },

    async loadSummary() {
      this.loading = true;
      try {
        const { data } = await this.$apollo.query({
          query: GET_DAILY_SHIPPING_SUMMARY,
          variables: {
            reportDate: this.getReportDateISO()
          },
          fetchPolicy: 'network-only'
        });
        this.summary = data.dailyShippingSummary;
      } catch (error) {
        console.error('Failed to load daily summary:', error);
        this.showSnackbar('Failed to load daily summary', 'error');
      } finally {
        this.loading = false;
      }
    },

    async loadShipments() {
      this.loadingShipments = true;
      try {
        const { data } = await this.$apollo.query({
          query: GET_DAILY_SHIPMENTS,
          variables: {
            reportDate: this.getReportDateISO(),
            carrierCode: this.carrierFilter,
            search: this.search || null,
            page: this.tableOptions.page,
            pageSize: this.tableOptions.itemsPerPage
          },
          fetchPolicy: 'network-only'
        });
        this.shipments = data.dailyShipments?.shipments || [];
        this.totalShipments = data.dailyShipments?.totalCount || 0;
      } catch (error) {
        console.error('Failed to load shipments:', error);
        this.showSnackbar('Failed to load shipments', 'error');
      } finally {
        this.loadingShipments = false;
      }
    },

    debouncedLoadShipments() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = window.setTimeout(() => {
        this.tableOptions.page = 1;
        this.loadShipments();
      }, 300);
    },

    openCreateManifestDialog(carrier: CarrierSummary) {
      this.selectedCarrier = carrier;
      this.manifestDialog = true;
    },

    /**
     * Fetch and display UPS Package Summary for printing
     */
    async showUpsPackageSummary() {
      this.loadingUpsSummary = true;
      try {
        const { data } = await this.$apollo.query({
          query: GET_UPS_PACKAGE_SUMMARY,
          variables: {
            reportDate: this.getReportDateISO()
          },
          fetchPolicy: 'network-only'
        });

        if (data.upsPackageSummary) {
          this.upsPackageSummary = data.upsPackageSummary;
          this.upsPackageSummaryDialog = true;
        } else {
          this.showSnackbar('No UPS shipments found for this date', 'warning');
        }
      } catch (error) {
        console.error('Failed to load UPS package summary:', error);
        this.showSnackbar('Failed to load UPS package summary', 'error');
      } finally {
        this.loadingUpsSummary = false;
      }
    },

    /**
     * Initialize hardware and printer services
     */
    async initializeHardwareServices() {
      try {
        this.hardwareService = getHardwareService();
        this.printerConfigService = getPrinterConfiguration();

        // Get configured printer info
        this.updateConfiguredPrinter();

        // Subscribe to configuration changes
        this.printerConfigService.onConfigurationChange(() => {
          this.updateConfiguredPrinter();
        });
      } catch (error) {
        console.warn('[EndOfDay] Failed to initialize hardware services:', error);
      }
    },

    /**
     * Update configured printer info for display
     */
    async updateConfiguredPrinter() {
      if (!this.printerConfigService || !this.hardwareService) return;

      this.configuredPrinterId = this.printerConfigService.getLabelPrinter();

      if (this.configuredPrinterId) {
        try {
          const printers = await this.hardwareService.discoverPrinters();
          const printer = printers.find(p => p.id === this.configuredPrinterId);
          this.configuredPrinterName = printer?.name || 'Configured Printer';
        } catch (error) {
          this.configuredPrinterName = 'Label Printer';
        }
      } else {
        this.configuredPrinterName = null;
      }
    },

    /**
     * Print the UPS Package Summary via browser (standard paper)
     */
    printUpsSummaryBrowser() {
      this.printOptionsMenu = false;
      this.printUpsSummary();
    },

    /**
     * Print the UPS Package Summary to configured label printer via UHB
     */
    async printUpsSummaryHardware() {
      this.printOptionsMenu = false;

      if (!this.configuredPrinterId || !this.hardwareService) {
        this.showSnackbar('No label printer configured. Please configure a printer in the hardware widget.', 'warning');
        return;
      }

      const printContent = this.$refs.upsSummaryPrint?.$el;
      if (!printContent) {
        this.showSnackbar('Unable to print - content not ready', 'error');
        return;
      }

      this.printingToHardware = true;

      try {
        // Generate a label-optimized PDF directly using jsPDF
        // Designed for 4x6" thermal label printers with large, readable fonts
        const jsPDF = (await import('jspdf')).default;
        const summary = this.upsPackageSummary;

        // Create PDF for 4x6 inch label (thermal printer standard)
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'in',
          format: [4, 6]
        });

        const pageWidth = 4;
        const margin = 0.15;
        let y = 0.2;

        // Helper function for text
        const addText = (text: string, x: number, yPos: number, options: any = {}) => {
          pdf.setFontSize(options.size || 12);
          pdf.setFont('helvetica', options.style || 'normal');
          pdf.text(text, x, yPos, options.align ? { align: options.align } : undefined);
        };

        // Title
        addText('UPS PACKAGE SUMMARY', pageWidth / 2, y, { size: 16, style: 'bold', align: 'center' });
        y += 0.35;

        // Horizontal line
        pdf.setLineWidth(0.02);
        pdf.line(margin, y, pageWidth - margin, y);
        y += 0.2;

        // Date and Account
        addText('DATE:', margin, y, { size: 11, style: 'bold' });
        const dateStr = new Date(summary.reportDate).toLocaleDateString('en-US', {
          month: 'short', day: '2-digit', year: 'numeric'
        }).toUpperCase();
        addText(dateStr, 0.7, y, { size: 11 });

        addText('ACCT:', 2.0, y, { size: 11, style: 'bold' });
        addText(summary.accountNumber || 'N/A', 2.5, y, { size: 11 });
        y += 0.25;

        // Shipper info
        addText(summary.shipperName || 'TEAMLEADER', margin, y, { size: 11, style: 'bold' });
        y += 0.2;
        if (summary.shipperStreet) {
          addText(summary.shipperStreet, margin, y, { size: 10 });
          y += 0.18;
          addText(`${summary.shipperCity} ${summary.shipperState} ${summary.shipperPostalCode}`, margin, y, { size: 10 });
          y += 0.25;
        }

        // Horizontal line
        pdf.line(margin, y, pageWidth - margin, y);
        y += 0.2;

        // DRIVER SUMMARY section (matching WorldShip layout)
        addText('DRIVER SUMMARY', margin, y, { size: 12, style: 'bold' });
        y += 0.3;

        // TOTAL NUMBER OF PACKAGES = X
        addText(`TOTAL NUMBER OF PACKAGES = ${summary.totalPackages}`, margin, y, { size: 11 });
        y += 0.3;

        // Service breakdown header
        addText('Service', margin, y, { size: 10, style: 'bold' });
        addText('Pkgs', 2.8, y, { size: 10, style: 'bold' });
        addText('Shpts', 3.4, y, { size: 10, style: 'bold' });
        y += 0.05;
        pdf.setLineWidth(0.01);
        pdf.line(margin, y, pageWidth - margin, y);
        y += 0.18;

        // Service breakdown rows
        const defaultServices = [
          { upsCode: '1DA', name: 'Next Day Air' },
          { upsCode: '2DA', name: '2nd Day Air' },
          { upsCode: '3DS', name: '3 Day Select' },
          { upsCode: 'GND', name: 'Ground' }
        ];

        const breakdownMap = new Map(
          (summary.serviceBreakdown || []).map((s: any) => [s.upsCode, s])
        );

        for (const svc of defaultServices) {
          const data = (breakdownMap.get(svc.upsCode) || { packageCount: 0, shipmentCount: 0 }) as { packageCount: number; shipmentCount: number };
          addText(`${svc.upsCode} (${svc.name})`, margin, y, { size: 10 });
          addText(String(data.packageCount || 0), 2.9, y, { size: 10 });
          addText(String(data.shipmentCount || 0), 3.5, y, { size: 10 });
          y += 0.2;
        }

        y += 0.1;

        // Horizontal line
        pdf.line(margin, y, pageWidth - margin, y);
        y += 0.2;

        // Signature section (matching WorldShip layout)
        // Received By: on its own line with full-width signature line
        addText('Received By:', margin, y, { size: 10, style: 'bold' });
        pdf.line(1.15, y + 0.02, pageWidth - margin, y + 0.02);
        y += 0.35;

        // Pickup Time and Pkgs on the same line
        addText('Pickup Time:', margin, y, { size: 10, style: 'bold' });
        pdf.line(1.1, y + 0.02, 2.1, y + 0.02);
        addText('Pkgs:', 2.3, y, { size: 10, style: 'bold' });
        pdf.line(2.7, y + 0.02, pageWidth - margin, y + 0.02);
        y += 0.3;

        // Footer
        y = 5.85;
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        const genTime = new Date().toLocaleString('en-US', {
          month: 'short', day: '2-digit', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true
        });
        pdf.text(`Generated: ${genTime}`, margin, y);
        pdf.text('TeamLeader OES', pageWidth - margin, y, { align: 'right' });

        // Convert to data URL (UHB requires HTTP/HTTPS/DATA URLs, not blob URLs)
        const pdfDataUrl = pdf.output('dataurlstring');

        // Send to hardware service for printing
        const result = await this.hardwareService.printLabelFromUrl(
          this.configuredPrinterId,
          pdfDataUrl
        );

        if (result.success) {
          this.showSnackbar('UPS Package Summary sent to printer', 'success');
        } else {
          this.showSnackbar(result.error || 'Failed to print to label printer', 'error');
        }
      } catch (error: any) {
        console.error('[EndOfDay] Hardware print failed:', error);
        this.showSnackbar(error.message || 'Failed to print to label printer', 'error');
      } finally {
        this.printingToHardware = false;
      }
    },

    /**
     * Print the UPS Package Summary in a clean print window
     */
    printUpsSummary() {
      const printContent = this.$refs.upsSummaryPrint?.$el?.innerHTML;
      if (!printContent) {
        this.showSnackbar('Unable to print - content not ready', 'error');
        return;
      }

      // Open a new window with just the summary content
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) {
        // Fallback to regular print if popup blocked
        this.$nextTick(() => window.print());
        return;
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>UPS Package Summary - ${this.reportDateFormatted}</title>
          <style>
            body {
              font-family: 'Courier New', Courier, monospace;
              background: white;
              color: black;
              margin: 0;
              padding: 20px;
            }
            .print-content { max-width: 800px; margin: 0 auto; }
            .summary-header { display: flex; justify-content: space-between; margin-bottom: 15px; }
            .report-title { font-size: 18px; font-weight: bold; margin: 0 0 10px 0; }
            .info-row { margin: 3px 0; }
            .info-row .label { font-weight: bold; margin-right: 8px; }
            .ups-logo { width: 60px; height: auto; }
            .divider { border-top: 2px solid #000; margin: 15px 0; }
            .section-title { font-size: 14px; font-weight: bold; margin: 0 0 10px 0; }
            .total-packages { font-size: 14px; margin-bottom: 15px; }
            .total-packages strong { font-size: 16px; }
            .service-breakdown-table { width: 100%; max-width: 400px; border-collapse: collapse; margin-bottom: 15px; }
            .service-breakdown-table th, .service-breakdown-table td { padding: 4px 8px; text-align: left; }
            .service-breakdown-table th { font-weight: bold; border-bottom: 1px solid #000; }
            .count-col, .shipment-col { text-align: center !important; }
            .service-name { font-size: 11px; color: #666; }
            .api-notice { display: flex; align-items: flex-start; gap: 10px; padding: 10px; background: #f5f5f5; border: 1px solid #ddd; }
            .notice-icon { width: 20px; height: 20px; border-radius: 50%; background: #1976d2; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 12px; }
            .notice-text { font-size: 11px; line-height: 1.4; }
            .ack-title { font-size: 11px; margin-bottom: 15px; }
            .signature-row { display: flex; align-items: flex-end; gap: 30px; margin-bottom: 20px; }
            .signature-field { display: flex; align-items: flex-end; flex: 1; }
            .packages-field { display: flex; align-items: flex-end; width: 120px; }
            .field-label { font-weight: bold; margin-right: 8px; white-space: nowrap; }
            .field-line { flex: 1; border-bottom: 1px solid #000; min-width: 150px; }
            .field-line.short { min-width: 60px; }
            .tracking-table { width: 100%; border-collapse: collapse; font-size: 11px; }
            .tracking-table th, .tracking-table td { padding: 4px 8px; text-align: left; border-bottom: 1px solid #ddd; }
            .tracking-table th { font-weight: bold; background: #f5f5f5; border-bottom: 2px solid #000; }
            .tracking-number { font-family: 'Courier New', Courier, monospace; font-size: 10px; }
            .footer { margin-top: 20px; display: flex; justify-content: space-between; font-size: 10px; color: #666; }
            @media print {
              body { margin: 0; padding: 10mm; }
              .api-notice { background: #f5f5f5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .notice-icon { background: #1976d2 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.onafterprint = function() { window.close(); };
              }, 250);
            };
          <` + `/script>
        </body>
        </html>
      `);
      printWindow.document.close();
    },

    async createManifest() {
      if (!this.selectedCarrier) return;

      this.creatingManifest = this.selectedCarrier.carrierCode;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: CREATE_CARRIER_MANIFEST,
          variables: {
            carrierId: this.selectedCarrier.carrierCode,
            shipDate: this.getReportDateISO(),
            labelIds: null // Include all unmanifested labels
          }
        });

        const result = data.createCarrierManifest;
        if (result.success) {
          this.showSnackbar(
            `Manifest created successfully with ${result.shipmentCount} shipments`,
            'success'
          );
          this.manifestDialog = false;
          // Reload data to reflect changes
          await this.loadSummary();
          await this.loadShipments();

          // If manifest download URL is available, open it
          if (result.manifestDownloadUrl) {
            window.open(result.manifestDownloadUrl, '_blank');
          }
        } else {
          this.showSnackbar(result.errorMessage || 'Failed to create manifest', 'error');
        }
      } catch (error: any) {
        console.error('Failed to create manifest:', error);
        this.showSnackbar(error.message || 'Failed to create manifest', 'error');
      } finally {
        this.creatingManifest = null;
      }
    },

    exportToCsv() {
      if (!this.summary || !this.shipments.length) return;

      const rows = [
        ['End of Day Report - ' + this.reportDateFormatted],
        [],
        ['Summary'],
        ['Labels Created', this.summary.labelsCreated],
        ['Labels Voided', this.summary.labelsVoided],
        ['Packages Shipped', this.summary.pickTicketsShipped],
        ['Total Shipping Cost', '$' + this.formatCurrency(this.summary.totalShippingCost)],
        [],
        ['Carrier Breakdown'],
        ['Carrier', 'Shipments', 'Cost', 'Manifest Status'],
        ...this.summary.carrierBreakdown.map(c => [
          c.carrierName,
          c.shipmentCount,
          '$' + this.formatCurrency(c.totalCost),
          this.getManifestStatusText(c.manifestStatus)
        ]),
        [],
        ['Shipments'],
        ['Time', 'Order #', 'Pick Ticket', 'Customer', 'Carrier', 'Tracking #', 'Cost', 'Status'],
        ...this.shipments.map(s => [
          this.formatTime(s.createdAt),
          s.orderNumber,
          'PT' + s.pickTicketId,
          s.customerName,
          s.carrierName || s.carrierCode,
          s.trackingNumber,
          '$' + this.formatCurrency(s.shippingCost),
          s.isVoided ? 'Voided' : s.isManifested ? 'Manifested' : 'Active'
        ])
      ];

      const csvContent = rows.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `end-of-day-${this.reportDate}.csv`;
      link.click();
    },

    formatCurrency(value: number): string {
      return (value || 0).toFixed(2);
    },

    formatTime(dateString: string): string {
      if (!dateString) return '';
      // The backend stores UTC time but incorrectly serializes with local timezone offset
      // Strip any timezone offset and treat the time as UTC, then convert to local
      const withoutOffset = dateString.replace(/[+-]\d{2}:\d{2}$/, '').replace(/Z$/, '');
      const utcDateString = withoutOffset + 'Z';
      const date = new Date(utcDateString);
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    },

    getCarrierLogo(carrierCode: string): string | null {
      if (!carrierCode) return null;
      const code = carrierCode.toLowerCase();
      // Check exact match first
      if (this.carrierLogos[code]) return this.carrierLogos[code];
      // Then check partial matches
      if (code.includes('ups')) return this.carrierLogos.ups;
      if (code.includes('fedex')) return this.carrierLogos.fedex;
      if (code.includes('usps') || code.includes('stamps')) return this.carrierLogos.usps;
      return null;
    },

    getCarrierColor(carrierCode: string): string {
      const colors: Record<string, string> = {
        ups: '#351C15',
        fedex: '#4D148C',
        usps: '#004B87',
        dhl: '#FFCC00'
      };
      return colors[carrierCode?.toLowerCase()] || 'grey';
    },

    getManifestStatusColor(status: string): string {
      const colors: Record<string, string> = {
        Complete: 'success',
        Pending: 'warning',
        Partial: 'orange',
        NotRequired: 'grey',
        RequiresWorldShip: 'info'
      };
      return colors[status] || 'grey';
    },

    getManifestStatusText(status: string): string {
      const texts: Record<string, string> = {
        Complete: 'Complete',
        Pending: 'Pending',
        Partial: 'Partial',
        NotRequired: 'Not Required',
        RequiresWorldShip: 'Print Summary'
      };
      return texts[status] || status;
    },

    showSnackbar(message: string, color: string) {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    }
  }
});
